import { KeyValuePipe, SlicePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, Input, OnInit } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatFormField } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { debounceTime, distinctUntilChanged, take } from 'rxjs/operators';
import { DashboardCard } from '../../../class/Preference';
import { Unit } from '../../../database/models';
import { DevicesManagerService, ImmobileDevice } from '../../../services/devices-manager.service';
import { LoadingService } from '../../../services/loading.service';
import { MapService } from '../../../services/map.service';
import { PermissionService } from '../../../services/permission.service';
import { PreferenceService } from '../../../services/preference.service';
import { RoutingService } from '../../../services/routing.service';
import UnitsService from '../../../services/units.service';
import { DEVICES_IMMOBILIZED } from '../../devices/devices-routing.constants';
import { DeviceCardComponent } from '../../shared/card/device-card/device-card.component';
import { WidgetLoaderComponent } from '../../shared/widget/widget-loader/widget-loader.component';
import { TreeComponent } from '../../tree/tree.component';

@Component({
  selector: 'widget-immobilized-device',
  templateUrl: './widget-immobilized-device.component.html',
  styleUrls: ['./widget-immobilized-device.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    TranslatePipe,
    MatIcon,
    SlicePipe,
    MatTooltipModule,
    WidgetLoaderComponent,
    DeviceCardComponent,
    TreeComponent,
    MatFormField,
    MatSelectModule,
    ReactiveFormsModule,
    KeyValuePipe,
  ],
})
export class WidgetImmobilizedDeviceComponent implements OnInit {
  @Input() card: DashboardCard;
  @Input() index: number;
  @Input() isEdit: boolean;

  private _unitsService = inject(UnitsService);
  public routingService = inject(RoutingService);
  public router = inject(Router);
  public mapService = inject(MapService);
  public preferenceService = inject(PreferenceService);
  public loadingService = inject(LoadingService);
  public permissionService = inject(PermissionService);
  private translateService = inject(TranslateService);
  private _devicesManagerService = inject(DevicesManagerService);
  public $devicesMap = this._devicesManagerService.$devicesMap;
  public $unitsById = this._unitsService.$unitsById;

  public changingService = false;
  public serviceId: number;
  public triplessDevices: ImmobileDevice[] = [];
  public timeImmobility = new FormControl(null);

  public timeOptions = {
    1: {
      text: this.translateService.instant('DATE.YESTERDAY').toLowerCase(),
    },
    6: {
      text: this.translateService.instant('DATE.TIME.FROM_7_DAYS'),
    },
    13: {
      text: this.translateService.instant('DATE.TIME.FROM_14_DAYS'),
    },
    29: {
      text: this.translateService.instant('DATE.TIME.FROM_30_DAYS'),
    },
    59: {
      text: this.translateService.instant('DATE.TIME.FROM_60_DAYS'),
    },
    89: {
      text: this.translateService.instant('DATE.TIME.FROM_90_DAYS'),
    },
  };

  ngOnInit() {
    if (this._unitsService.$unitsById() && this._devicesManagerService.$devicesMap()) {
      this.initComponent();
    } else {
      this.loadingService.allLoadedEvent$.pipe(take(1)).subscribe(() => {
        this.initComponent();
      });
    }
  }

  private initComponent() {
    this.timeImmobility.setValue(this.card.time ? this.card.time : 1);
    this.serviceId = this.card.uid_service ? this.card.uid_service : 0;
    this.triplessDevices = this._devicesManagerService.setImmobileDevicesCount(
      this.serviceId,
      this.timeImmobility.value
    );

    this.timeImmobility.valueChanges.pipe(debounceTime(200), distinctUntilChanged()).subscribe(value => {
      this.updateCount(value);
    });
  }

  public updateCount(value: number) {
    this.triplessDevices = this._devicesManagerService.setImmobileDevicesCount(this.serviceId, value);
    this.setPreferenceTime();
  }

  public resetServices() {
    this.changingService = false;
    this.serviceId = 0;
    this.triplessDevices = this._devicesManagerService.setImmobileDevicesCount(
      this.serviceId,
      this.timeImmobility.value
    );
  }

  public changeParent(units: Unit[]) {
    if (units[0]?.uid !== this.serviceId) {
      if (units[0]?.uid === 0) {
        this.resetServices();
      } else {
        this.serviceId = units[0]?.uid;
        this.toggleSelectService();
        this.triplessDevices = this._devicesManagerService.setImmobileDevicesCount(
          this.serviceId,
          this.timeImmobility.value
        );
      }
      this.setPreferenceUid();
    }
  }

  public goToTriplessDevice() {
    this.router.navigate([DEVICES_IMMOBILIZED], {
      queryParams: {
        uid_unit: this.serviceId,
        time: this.timeImmobility.value,
      },
    });
  }

  private setPreferenceUid() {
    this.preferenceService.mutatePreference(preference => {
      preference.dashboard[this.index].uid_service = this.serviceId;
    });
    this.savePreference();
  }

  private setPreferenceTime() {
    this.preferenceService.mutatePreference(preference => {
      preference.dashboard[this.index].time = this.timeImmobility.value;
    });
    this.savePreference();
  }

  private savePreference() {
    this.preferenceService.savePreference('update');
    this.preferenceService.updateStorage();
  }

  public toggleSelectService() {
    this.changingService = !this.changingService;
  }
}
